// SETU Incentive Policy calculation logic
// Based on SETU_Incentive_Policy_Calculator excel

export const MONTH_LABELS = [
  "Apr", "May", "Jun", "Jul", "Aug", "Sep",
  "Oct", "Nov", "Dec", "Jan", "Feb", "Mar",
];

// Core drivers
export const REV_MULTIPLE_15 = 15;
export const REV_MULTIPLE_11 = 11;
export const REV_MULTIPLE_BREAKEVEN = 10;
export const PAYOUT_RATE = 0.10; // 10% of revenue as total pool

export const S2_FIXED_PCT = 0.70;
export const S2_VARIABLE_PCT = 0.30;

export function computeSummary(monthlyComp, historicalRate = 0.70) {
  const monthly = Number(monthlyComp) || 0;
  const annual = monthly * 12;

  // Revenue targets
  const target15 = annual * REV_MULTIPLE_15;
  const target11 = annual * REV_MULTIPLE_11;
  const breakEven = annual * REV_MULTIPLE_BREAKEVEN;
  const expectedRev = target15 * historicalRate;

  // ---------- Scenario 1: Current pay + balance incentive ----------
  // Total pool @15x = 10% of 15x target = annual * 1.5
  const s1_pool_annual = target15 * PAYOUT_RATE;
  const s1_fixed_annual = annual; // full base already paid
  const s1_incentive_annual = Math.max(0, s1_pool_annual - s1_fixed_annual); // = annual * 0.5
  const s1_total_annual = s1_fixed_annual + s1_incentive_annual;

  const s1_fixed_monthly = s1_fixed_annual / 12;
  const s1_incentive_monthly = s1_incentive_annual / 12;
  const s1_total_monthly = s1_total_annual / 12;

  // ---------- Scenario 2: Counter Offer (70/30) ----------
  const s2_fixed_annual = annual * S2_FIXED_PCT;
  const s2_variable_annual = annual * S2_VARIABLE_PCT;

  // 11x pool = 10% of 11x target
  const s2_pool_11x = target11 * PAYOUT_RATE;
  // Counter-offer incentive @ 11x = 11x pool - fixed
  const s2_incentive_11x = Math.max(0, s2_pool_11x - s2_fixed_annual);

  const s2_total_at_11x = s2_fixed_annual + s2_incentive_11x;
  const s2_total_at_15x = s2_fixed_annual + s2_incentive_11x + s2_variable_annual;

  const s2_fixed_monthly = s2_fixed_annual / 12;
  const s2_variable_monthly = s2_variable_annual / 12;
  const s2_incentive_monthly_11x = s2_incentive_11x / 12;

  return {
    monthly,
    annual,
    target15,
    target11,
    breakEven,
    expectedRev,
    historicalRate,
    monthlyTarget15: target15 / 12,
    monthlyTarget11: target11 / 12,

    scenario1: {
      pool_annual: s1_pool_annual,
      fixed_annual: s1_fixed_annual,
      incentive_annual: s1_incentive_annual,
      total_annual: s1_total_annual,
      fixed_monthly: s1_fixed_monthly,
      incentive_monthly: s1_incentive_monthly,
      total_monthly: s1_total_monthly,
    },
    scenario2: {
      fixed_annual: s2_fixed_annual,
      variable_annual: s2_variable_annual,
      pool_11x: s2_pool_11x,
      incentive_11x_annual: s2_incentive_11x,
      total_at_11x: s2_total_at_11x,
      total_at_15x: s2_total_at_15x,
      fixed_monthly: s2_fixed_monthly,
      variable_monthly: s2_variable_monthly,
      incentive_monthly_11x: s2_incentive_monthly_11x,
    },
  };
}

// Compute the full 12-month tracker table
export function computeTracker(summary, actuals /* array of 12 numbers */) {
  const monthly15 = summary.monthlyTarget15;
  const monthly11 = summary.monthlyTarget11;
  const annualIncentiveS1 = summary.scenario1.incentive_annual;
  const monthlyIncentiveS1Max = annualIncentiveS1 / 12;

  const s2_fixed_month = summary.scenario2.fixed_monthly;
  const s2_var_month = summary.scenario2.variable_monthly;
  const s2_inc_11x_month = summary.scenario2.incentive_monthly_11x;
  const s1_fixed_month = summary.scenario1.fixed_monthly;

  let cumTarget15 = 0;
  let cumTarget11 = 0;
  let cumActual = 0;
  let cumS1Incentive = 0;

  const rows = MONTH_LABELS.map((label, idx) => {
    const actual = Number(actuals[idx]) || 0;
    cumTarget15 += monthly15;
    cumTarget11 += monthly11;
    cumActual += actual;

    // Target Achievement % (cumulative vs 15x cumulative target)
    const achievementPct = cumTarget15 > 0 ? (cumActual / cumTarget15) * 100 : 0;
    // Revenue multiple (cumulative actual / annual comp)
    const revenueMultiple = summary.annual > 0 ? cumActual / summary.annual : 0;

    // Scenario 1 pro-rata incentive for this month = (actual_month / monthly_target_15x) * max monthly incentive
    const monthAchievementRatio = monthly15 > 0 ? actual / monthly15 : 0;
    const s1_pro_rata = monthAchievementRatio * monthlyIncentiveS1Max;
    cumS1Incentive += s1_pro_rata;
    const s1_total = s1_fixed_month + s1_pro_rata;

    // Scenario 2 - Variable @15x: eligible only if cumulative achievement >= 15x cumulative target
    const eligible15x = cumActual >= cumTarget15;
    const s2_var_pay = eligible15x ? s2_var_month : 0;

    // Scenario 2 - Incentive @11x: eligible only if cumulative achievement >= 11x cumulative target
    const eligible11x = cumActual >= cumTarget11;
    const s2_incentive_11x = eligible11x ? s2_inc_11x_month : 0;

    const s2_total = s2_fixed_month + s2_var_pay + s2_incentive_11x;

    const variance = cumActual - cumTarget15;

    let status = "Below Trend";
    if (achievementPct >= 100) status = "On Target";
    else if (achievementPct >= 73) status = "Approaching (11x)"; // 11/15 ≈ 73%
    else if (achievementPct >= 67) status = "Break-even Zone";

    return {
      month: label,
      monthlyTarget15: monthly15,
      monthlyTarget11: monthly11,
      cumulativeTarget: cumTarget15,
      actualRevenue: actual,
      cumulativeActual: cumActual,
      achievementPct,
      revenueMultiple,
      s1_monthly_payout: s1_fixed_month,
      s1_pro_rata_incentive: s1_pro_rata,
      s1_incentive_payable: s1_pro_rata,
      s1_total_earnings: s1_total,
      s2_monthly_payout: s2_fixed_month,
      s2_variable_15x: s2_var_pay,
      s2_incentive_11x: s2_incentive_11x,
      s2_total_earnings: s2_total,
      variance,
      status,
    };
  });

  const totals = rows.reduce(
    (acc, r) => {
      acc.actualRevenue += r.actualRevenue;
      acc.s1_monthly_payout += r.s1_monthly_payout;
      acc.s1_pro_rata_incentive += r.s1_pro_rata_incentive;
      acc.s1_total_earnings += r.s1_total_earnings;
      acc.s2_monthly_payout += r.s2_monthly_payout;
      acc.s2_variable_15x += r.s2_variable_15x;
      acc.s2_incentive_11x += r.s2_incentive_11x;
      acc.s2_total_earnings += r.s2_total_earnings;
      return acc;
    },
    {
      actualRevenue: 0,
      s1_monthly_payout: 0,
      s1_pro_rata_incentive: 0,
      s1_total_earnings: 0,
      s2_monthly_payout: 0,
      s2_variable_15x: 0,
      s2_incentive_11x: 0,
      s2_total_earnings: 0,
    }
  );

  return { rows, totals };
}
